import React from 'react';

import { AnyAri } from '@atlassian/ari/any-ari';
import { mapInviteResult } from '@atlassian/expansion-services/services';
import type { InviteInput, InviteResult } from '@atlassian/expansion-services/services';

import type { InviteOnThirdPartyLinkUser, InviteOnThirdPartyLinkUsers, ShownInviteFlag, SourceType } from '../common/types';
import type {
	PiSlackLinkEligibilityResult,
	ThirdPartyEligibilityUser,
	FetchEligibleEmailsResponse,
} from '../services/third-party-link/types';

import {
	InviteOnThirdPartyLinkFlag,
	type Props as InviteOnThirdPartyLinkFlagProps,
} from './invite-on-third-party-link-flag/main';
import {
	InviteOnThirdPartyLinkInvitesSentFlag,
	type Props as InviteOnThirdPartyLinkInvitesSentFlagProps,
} from './invite-on-third-party-link-invites-sent-flag/main';

type RenderFlagProps = Pick<InviteOnThirdPartyLinkFlagProps, 'onDismiss'>;

type FlagRenderer = (args: {
	id: string;
	render: (props: RenderFlagProps) => React.JSX.Element;
}) => void;

type DismissFlag = (id: string) => void;

type InviteFn = (input: InviteInput) => Promise<InviteResult[]>;

/**
 * Analytics observer invoked when a user clicks the invite CTA on an
 * invite flag.
 */
export type OnInviteActioned = (source: SourceType, flagId: string) => void;

export type ShowInviteOnThirdPartyLinkFlagArgs = {
	cloudId: string;
	dismissFlag: DismissFlag;
	flagId: string;
	invite: InviteFn;
	onActioned?: OnInviteActioned;
	productName: 'Jira' | 'Confluence';
	showFlag: FlagRenderer;
	source: SourceType;
	spaceName?: string;
	usersWithoutAccess: InviteOnThirdPartyLinkUsers;
};

const resourceOwnerByProduct: Record<
	ShowInviteOnThirdPartyLinkFlagArgs['productName'],
	'jira-software' | 'confluence'
> = {
	Jira: 'jira-software',
	Confluence: 'confluence',
};

export const showInviteOnThirdPartyLinkFlag = ({
	flagId,
	cloudId,
	onActioned,
	productName,
	source,
	usersWithoutAccess,
	showFlag,
	dismissFlag,
	spaceName,
	invite,
}: ShowInviteOnThirdPartyLinkFlagArgs): void => {
	showFlag({
		id: flagId,
		render: (props) => {
			const inviteFlagProps: Pick<InviteOnThirdPartyLinkFlagProps, 'id' | 'onDismiss'> = {
				id: flagId,
				onDismiss: (_id, _dismissMethod) => dismissFlag(flagId),
			};

			return (
				<InviteOnThirdPartyLinkFlag
					{...inviteFlagProps}
					{...props}
					onActioned={onActioned ? () => onActioned(source, flagId) : undefined}
					spaceName={spaceName}
					productName={productName}
					source={source}
					usersWithoutAccess={usersWithoutAccess}
					onInvite={async () => {
						let response: Awaited<ReturnType<typeof invite>>;
						try {
							response = await invite({
								users: usersWithoutAccess.map((user) => ({
									...(user.email ? { email: user.email } : {}),
								})),
								integration: 'twcg-853-3p-in-1p-link',
								// GROOT-1304 Add enablement touchpoint in expansion services https://hello.jira.atlassian.cloud/browse/GROOT-1304
								touchpoint: 'unknown',
								resources: cloudId
									? [
											AnyAri.create({
												resourceOwner: resourceOwnerByProduct[productName],
												resourceType: 'site',
												resourceId: cloudId,
											}),
										]
									: [],
							});
						} catch {
							return;
						}

						const { error, failure, invited, requested } = mapInviteResult(response);
						if (error.length > 0 || failure) {
							return;
						}

						dismissFlag(flagId);
						showFlag({
							id: `${flagId}-sent`,
							render: ({ onDismiss: _ignoredOnDismiss, ...sentProps }) => {
								const sentFlagProps: Pick<
									InviteOnThirdPartyLinkInvitesSentFlagProps,
									'id' | 'onDismiss' | 'hasRequestAccess' | 'inviteCount' | 'productName'
								> = {
									id: `${flagId}-sent`,
									onDismiss: () => dismissFlag(`${flagId}-sent`),
									hasRequestAccess: requested.length > 0,
									inviteCount: invited.length + requested.length,
									productName,
								};
								return <InviteOnThirdPartyLinkInvitesSentFlag {...sentProps} {...sentFlagProps} />;
							},
						});
					}}
				/>
			);
		},
	});
};

export const toInviteUsers = (
	users: ThirdPartyEligibilityUser[] = [],
): InviteOnThirdPartyLinkUsers =>
	users
		.filter((user) => Boolean(user.email))
		.map((user) => ({
			id: user.atlassianAccountId || user.email,
			email: user.email,
			name: user?.displayName,
			avatar: user.avatarUrl,
		}));

export type ShowInviteFlagsArgs = {
	cloudId?: string | null;
	dismissFlag: DismissFlag;
	eligibleEmailsResults: FetchEligibleEmailsResponse;
	invite: InviteFn;
	onActioned?: OnInviteActioned;
	piEligibilityResults: PiSlackLinkEligibilityResult[];
	productName: 'Jira' | 'Confluence';
	showFlag: FlagRenderer;
	spaceName: string;
};

/**
 * Shows one invite flag per eligible Slack link, and one grouped invite flag
 * for all eligible mailto users. Returns an array of shown flags if at least one flag was shown.
 */
export const showInviteFlags = ({
	cloudId,
	dismissFlag,
	eligibleEmailsResults,
	invite,
	onActioned,
	piEligibilityResults,
	productName,
	spaceName,
	showFlag,
}: ShowInviteFlagsArgs): ShownInviteFlag[] => {
	let shown: ShownInviteFlag[] = [];

	// One flag per Slack link that returned eligible users
	piEligibilityResults.forEach(({ eligibleUsers }, i) => {
		if (!eligibleUsers?.length) return;
		const candidates = toInviteUsers(eligibleUsers);
		if (candidates.length === 0) return;
		showInviteOnThirdPartyLinkFlag({
			flagId: `invite-on-third-party-slack-link-${i}`,
			cloudId: cloudId ?? '',
			productName,
			source: 'slack',
			usersWithoutAccess: candidates,
			showFlag,
			dismissFlag,
			invite,
			onActioned,
			spaceName,
		});
		shown.push({ flagId: `invite-on-third-party-slack-link-${i}`, source: 'slack', userCount: candidates.length });
	});

	// One grouped flag for all eligible mailto users
	const mailtoUsers = eligibleEmailsResults?.eligibleEmails?.map((user) => {
		const thirdPartyUser: InviteOnThirdPartyLinkUser = {
			email: user.email,
			avatar: user.avatarUrl,
			name: user?.email,
			id: user.email,
		};
		return thirdPartyUser;
	});
	if (mailtoUsers && mailtoUsers.length > 0) {
		showInviteOnThirdPartyLinkFlag({
			flagId: 'invite-on-third-party-mailto-link',
			cloudId: cloudId ?? '',
			productName,
			source: 'mailto',
			usersWithoutAccess: mailtoUsers,
			showFlag,
			dismissFlag,
			invite,
			onActioned,
			spaceName,
		});
		shown.push({ flagId: 'invite-on-third-party-mailto-link', source: 'mailto', userCount: mailtoUsers.length });
	}

	return shown;
};
